import type {
  CompetitionViewerStanding,
  PayoutWalletState,
  TradingCompetitionPhase,
} from '@/components/social/competition/types';

export type CompetitionRankBarState =
  | { kind: 'signed-out' }
  | { kind: 'saving'; address: string }
  | { kind: 'error'; address: string; message: string | null }
  | { kind: 'entered'; wallet: string | null }
  | { kind: 'unranked'; wallet: string | null }
  | {
      kind: 'ranked';
      rank: number;
      value: string | number;
      wallet: string | null;
      winningsD6: string | null;
    }
  | {
      kind: 'final';
      rank: number;
      value: string | number;
      wallet: string | null;
      inPrizes: boolean;
      winningsD6: string | null;
    };

export interface ResolveRankBarStateInput {
  isAuthenticated: boolean;
  phase: TradingCompetitionPhase;
  standing: CompetitionViewerStanding;
  wallet: PayoutWalletState;
}

// Everyone who trades is entered automatically; the payout wallet only decides
// where a prize is sent. The bar therefore never gates on the wallet — it
// reports the standing and prompts for the wallet when one is missing.
export function resolveCompetitionRankBarState({
  isAuthenticated,
  phase,
  standing,
  wallet,
}: ResolveRankBarStateInput): CompetitionRankBarState {
  if (!isAuthenticated) return { kind: 'signed-out' };
  if (wallet.status === 'saving' && wallet.address !== null) {
    return { kind: 'saving', address: wallet.address };
  }
  if (wallet.status === 'error') {
    return { kind: 'error', address: wallet.address ?? '', message: wallet.message };
  }
  const saved = wallet.status === 'saved' ? wallet.address : null;
  if (phase === 'upcoming') return { kind: 'entered', wallet: saved };
  if (standing.rank === null || standing.value === null) return { kind: 'unranked', wallet: saved };
  if (phase === 'concluded') {
    return {
      kind: 'final',
      rank: standing.rank,
      value: standing.value,
      wallet: saved,
      // The paid set is whatever the API priced, not a place number: a tie can
      // pool places, and only the server knows what the row is actually owed.
      inPrizes: standing.predictedWinningsD6 !== null && standing.predictedWinningsD6 !== '0',
      winningsD6: standing.predictedWinningsD6,
    };
  }
  return {
    kind: 'ranked',
    rank: standing.rank,
    value: standing.value,
    wallet: saved,
    winningsD6: standing.predictedWinningsD6,
  };
}
